
// This file contains the Supabase client and helper functions
import { createClient } from '@supabase/supabase-js';
import { QueryClient } from '@tanstack/react-query';
import type { Database } from './types';

const SUPABASE_URL = "https://hbgmfqtsdizjedoanieo.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhiZ21mcXRzZGl6amVkb2FuaWVvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQ4MjY0MTgsImV4cCI6MjA2MDQwMjQxOH0.S6w7v1GkO9e7TwQZT4jaNcwWveofgT5OTWqYmlEBb7Q";

export type TypedSupabaseClient = ReturnType<typeof createClient<Database>>;

// Create and export the Supabase client with explicit auth configuration
export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storage: localStorage,
    detectSessionInUrl: true,
    flowType: 'implicit',
  },
  global: {
    headers: {
      'Cache-Control': 'no-store',
    },
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Debug helper function to check token state
export const checkTokenExpiry = () => {
  const authData = localStorage.getItem('sb-' + SUPABASE_URL.split('//')[1].split('.')[0] + '-auth-token');
  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      const expiresAt = parsed.expires_at;
      const now = Math.floor(Date.now() / 1000);
      const timeLeft = expiresAt - now;
      
      console.group('🔑 Auth Token Check');
      console.log('Token exists in localStorage');
      console.log('Expires at:', new Date(expiresAt * 1000).toLocaleString());
      console.log('Time left:', Math.floor(timeLeft / 60), 'minutes');
      console.log('Token valid:', timeLeft > 0 ? 'Yes' : 'No');
      console.groupEnd();
      
      return { valid: timeLeft > 0, expiresIn: timeLeft };
    } catch (err) {
      console.error('Error parsing auth data:', err);
      return { valid: false, error: 'Parse error' };
    }
  }
  return { valid: false, error: 'No token found' };
};

// Make debug function available globally
if (process.env.NODE_ENV !== 'production') {
  // @ts-ignore
  window.checkToken = checkTokenExpiry;
}

// Create a clean query client instance for React Query
export const createCleanQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30, // 30 minutes
        retry: 1,
      },
    },
  });
};

// Helper function to manually invalidate React Query cache keys
export const invalidateQueryCache = (queryClient: QueryClient, keys: string | string[] | any[]) => {
  if (typeof keys === 'string') {
    queryClient.invalidateQueries({ queryKey: [keys] });
  } else {
    keys.forEach((key) => {
      if (Array.isArray(key)) {
        queryClient.invalidateQueries({ queryKey: key });
      } else {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    });
  }
  console.log(`Cache invalidated for keys: ${Array.isArray(keys) ? keys.join(', ') : keys}`);
};

// Helper function to clear the entire React Query cache
export const clearQueryCache = (queryClient: QueryClient) => {
  queryClient.clear();
  console.log('Entire query cache cleared');
};

// Helper function to update profile with metadata
export const updateProfileFromAuth = async (userId: string) => {
  try {
    // Get current auth user data
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (!user || user.id !== userId) {
      console.error("User ID mismatch or user not found");
      return false;
    }
    
    // Extract name from metadata (note the corrected property name)
    const name = user.user_metadata?.full_name || 
                 user.user_metadata?.name || 
                 user.user_metadata?.preferred_name;
    
    if (!name) {
      console.log("No name found in user metadata");
      return false;
    }
    
    // Update profile with name from auth
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ full_name: name })
      .eq('id', userId);
    
    if (updateError) {
      console.error("Error updating profile from auth:", updateError);
      return false;
    }
    
    console.log("Profile updated with name from auth:", name);
    return true;
  } catch (error) {
    console.error("Error in updateProfileFromAuth:", error);
    return false;
  }
};

// Add debug logger function
export const debugAuth = async () => {
  console.groupCollapsed("[SUPABASE DEBUG] Checking current session status");
  
  try {
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error("[SUPABASE DEBUG] Error getting session:", error);
      console.groupEnd();
      return { error };
    }
    
    console.log("[SUPABASE DEBUG] Current session:", {
      exists: !!data.session,
      userId: data.session?.user?.id,
      expiresAt: data.session?.expires_at,
      tokenDetails: data.session ? {
        accessTokenExpiry: new Date(data.session?.expires_at * 1000).toISOString(),
        refreshTokenHasExpiry: !!data.session?.refresh_token,
        provider: data.session?.user?.app_metadata?.provider
      } : null
    });
    
    console.groupEnd();
    return { session: data.session };
  } catch (error) {
    console.error("[SUPABASE DEBUG] Unexpected error during session check:", error);
    console.groupEnd();
    return { error };
  }
};

// Call the debug function once to log initial state
debugAuth().catch(err => console.error("[SUPABASE DEBUG] Failed to run initial debug:", err));

// Flag to track if we've seen the initial session check
let initialSessionProcessed = false;

// Update the setupAuthStateListener function for more reliable session tracking
export const setupAuthStateListener = (queryClient: QueryClient) => {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    (event, session) => {
      console.group('[Global Auth State Change]');
      console.log('• event:', event);
      console.log('• session exists:', !!session);
      console.log('• user id:', session?.user?.id);
      console.log('• initial session processed:', initialSessionProcessed);
      console.groupEnd();
      
      if (event === 'INITIAL_SESSION') {
        initialSessionProcessed = true;
        console.log('[Global Auth] INITIAL_SESSION → initializing');
        return;
      }
      
      // CRITICAL FIX: Only clear cache on actual SIGNED_OUT events *after* we've processed the initial session
      if (event === 'SIGNED_OUT') {
        if (initialSessionProcessed) {
          console.group('[Global Auth] Genuine SIGNED_OUT → clearing caches');
          console.trace(); 
          queryClient.clear();
          console.groupEnd();
        } else {
          console.log('[Global Auth] Ignoring initial SIGNED_OUT event');
        }
      } else if (event === 'SIGNED_IN') {
        console.log('[Global Auth] SIGNED_IN → session established');
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('[Global Auth] TOKEN_REFRESHED → session extended');
      }
    }
  );
  
  return subscription;
};

// Safe sign out function that logs before signing out
export const safeSignOut = async () => {
  console.group('[auth] Manual signOut() call');
  console.trace(); // Show exactly where this is being called from
  try {
    await supabase.auth.signOut();
    console.log('[auth] signOut() completed successfully');
  } catch (error) {
    console.error('[auth] signOut() failed:', error);
  }
  console.groupEnd();
};
